/**
 * RSEV-87: Original Traffic Source & User Status Tracking.
 *
 * Captures the first/last-touch marketing attribution that
 * ads.reachstream.com attaches to every link into this app - as URL params
 * on landing, or (fallback) as the rs_ft/rs_lt cookies it sets on
 * .reachstream.com. Captured once, on app boot, before any route/auth
 * redirect can drop the query string, and kept in sessionStorage until the
 * signup request reads it. See the "ReachStream Attribution Data"
 * integration guide for the exact param/cookie shapes this mirrors.
 */

export interface AttributionRecord {
  channel?: string;
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  landing?: string;
  referrer?: string;
  ts?: string;
  lt_channel?: string;
  lt_source?: string;
  lt_medium?: string;
  lt_campaign?: string;
  gclid?: string;
  fbclid?: string;
  li_fat_id?: string;
  msclkid?: string;
}

const SESSION_KEY = "rs_attribution";
const MAX_LEN = 255;

// URL param name -> AttributionRecord key. utm_id/utm_term/utm_content map
// to standard UTM names; rs_* are ReachStream's own extras (rs_channel is
// already pre-classified into one of 12 fixed values server-side).
const URL_PARAM_MAP: Record<string, keyof AttributionRecord> = {
  rs_channel: "channel",
  utm_source: "source",
  utm_medium: "medium",
  utm_campaign: "campaign",
  utm_term: "term",
  utm_content: "content",
  rs_landing: "landing",
  rs_referrer: "referrer",
  rs_ft_ts: "ts",
  rs_lt_channel: "lt_channel",
  rs_lt_source: "lt_source",
  rs_lt_medium: "lt_medium",
  rs_lt_campaign: "lt_campaign",
  gclid: "gclid",
  fbclid: "fbclid",
  li_fat_id: "li_fat_id",
  msclkid: "msclkid",
};

function readFromUrl(): AttributionRecord | null {
  const params = new URLSearchParams(window.location.search);
  const record: AttributionRecord = {};
  let found = false;
  (Object.keys(URL_PARAM_MAP) as Array<keyof typeof URL_PARAM_MAP>).forEach((param) => {
    const value = params.get(param);
    if (value) {
      (record as Record<string, string>)[URL_PARAM_MAP[param]] = value.slice(0, MAX_LEN);
      found = true;
    }
  });
  return found ? record : null;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function readFromCookies(): AttributionRecord | null {
  const raw = readCookie("rs_ft");
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AttributionRecord;
  } catch {
    return null;
  }
}

/**
 * Call once, on app boot, before any route/auth redirect runs. Write-once:
 * a later page with no params/cookies of its own must never erase what an
 * earlier landing page already captured this session.
 */
export function captureAttributionOnce(): void {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const record = readFromUrl() ?? readFromCookies();
    if (record) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(record));
    }
  } catch {
    // sessionStorage can throw in a locked-down browser context - attribution
    // is best-effort, never worth breaking the app over.
  }
}

export function getSavedAttribution(): AttributionRecord | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as AttributionRecord) : null;
  } catch {
    return null;
  }
}
